/**
 * @fileoverview Market page — safe display formatters
 * @module utils/marketFormatters
 *
 * Every formatter here returns the literal string '—' for null/undefined/NaN
 * input so the UI never renders "NaN", "undefined", or a blank cell.
 */

const DASH = '—';

function isFiniteNumber(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

/** @param {number|null} n - dollar amount */
export function formatMoney(n) {
  if (!isFiniteNumber(n)) return DASH;
  return '$' + Math.round(n).toLocaleString('en-AU');
}

/**
 * @param {number|null} n - fraction (0-1), e.g. 0.42 -> "42%"
 * @param {number} digits - decimal places
 */
export function formatPct(n, digits = 0) {
  if (!isFiniteNumber(n)) return DASH;
  return (n * 100).toFixed(digits) + '%';
}

/** @param {number|null} n - e.g. 1.85 -> "1.85x" */
export function formatMultiple(n) {
  if (!isFiniteNumber(n)) return DASH;
  return n.toFixed(2) + 'x';
}

/** @param {number|null} n - plain integer count */
export function formatNumber(n) {
  if (!isFiniteNumber(n)) return DASH;
  return Math.round(n).toLocaleString('en-AU');
}

/** @param {number|null} n - rating, 1-99 */
export function formatRating(n) {
  if (!isFiniteNumber(n)) return DASH;
  return String(Math.round(n));
}

/** @param {number|null} price - share price in dollars per 1% */
export function formatSharePrice(price) {
  if (!isFiniteNumber(price) || price <= 0) return DASH;
  return '$' + price.toFixed(2);
}

/** @param {number|null} position - 1st, 2nd, etc. Null = not yet resulted. */
export function formatPlacing(position) {
  if (!isFiniteNumber(position) || position <= 0) return DASH;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = position % 100;
  return position + (s[(v - 20) % 10] || s[v] || s[0]);
}

export const SAMPLE_SIZE_COPY =
  'Minimum 10 settled runners required for ranked jockey/trainer tables.';

const RATING_BAND_TONES = {
  '90+': 'gold',
  '80-89': 'green',
  '70-79': 'blue',
  '60-69': 'muted',
  'Below 60': 'muted',
  Unknown: 'muted',
};

/** CSS tone suffix (mk-pill--<tone>) for a rating band badge. */
export function getRatingBandTone(band) {
  return RATING_BAND_TONES[band] || 'muted';
}

const VALUE_BUCKET_TONES = {
  Bargain: 'green',
  Value: 'blue',
  Fair: 'muted',
  Premium: 'gold',
  Risk: 'red',
  Unknown: 'muted',
};

/** CSS tone suffix (mk-pill--<tone>) for a value badge pill. */
export function getValueBucketTone(label) {
  return VALUE_BUCKET_TONES[label] || 'muted';
}

export { DASH };
